import React, { useMemo } from 'react';
import { Translation } from '~/components/translation/types';
import { RecommendedBetDto } from '~/components/sportsbook/lobby/api/sbapi/sbapi-api-model';
import BetRecommendationsTextKeys from './sportsbook-bet-recommendations-bet-text-keys';
import trimLeagueName from '../../../utils/trim-league-name';
import useSportsbookRoutes from '../../../hooks/use-sportsbook-routes';
import { Link } from '~/utils/navigation';
import styled from 'styled-components';
import { LabelBold } from '@comeon/ui';

const TitleLink = styled(Link)`
	cursor: pointer;
	@media (hover: hover) and (pointer: fine) {
		&:hover {
			color: var(--main-text-primary);
		}
	}
`;

const TitleLabel = styled(LabelBold)`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: inherit;
`;

const BetRecommendationsBetTitle: React.FC<{
	bet: RecommendedBetDto;
	shouldDeepLink: boolean;
	translations: Translation;
}> = ({ bet, shouldDeepLink, translations }) => {
	const sportsbookRoutes = useSportsbookRoutes();

	const [label, link] = useMemo(() => {
		const isCombo = bet.items.length > 1;
		const firstItem = bet.items[0];
		const firstEvent = firstItem.event;

		const sportLink = sportsbookRoutes.getSportPageLink(firstEvent.sportId, firstEvent.sportName, undefined, true);
		const leagueLink = sportsbookRoutes.getLeaguePageLink(
			firstEvent.sportId,
			firstEvent.sportName,
			firstEvent.leagueId,
			firstEvent.leagueName,
			undefined,
			true
		);
		const eventLink = sportsbookRoutes.getEventPageLink(
			firstEvent.sportId,
			firstEvent.sportName,
			firstEvent.leagueId,
			firstEvent.leagueName,
			firstEvent.id,
			firstEvent.eventName,
			undefined,
			true
		);

		// Single precanned
		if (!isCombo && firstItem.market.marketType.isPreCanned) {
			return [firstItem.event.eventName, eventLink];
		}

		// Combo of the same league
		if (isCombo && bet.items.every(item => item.event.leagueId === firstItem.event.leagueId)) {
			return [
				`${trimLeagueName(firstItem.event.leagueName)} - ${
					translations[BetRecommendationsTextKeys.BET_RECOMMENDATIONS_TITLE_COMBO_X]
				}`,
				leagueLink
			];
		}

		// Combo of the same sport
		if (isCombo && bet.items.every(item => item.event.sportId === firstItem.event.sportId)) {
			return [
				`${bet.items[0].event.sportName} - ${
					translations[BetRecommendationsTextKeys.BET_RECOMMENDATIONS_TITLE_COMBO_X]
				}`,
				sportLink
			];
		}

		// Combo of a several of sports, no link
		if (isCombo) {
			return [
				`${translations[BetRecommendationsTextKeys.BET_RECOMMENDATIONS_TITLE_MULTI_SPORT]} - ${
					translations[BetRecommendationsTextKeys.BET_RECOMMENDATIONS_TITLE_COMBO_X]
				}`
			];
		}
		// Single
		return [trimLeagueName(firstItem.event.leagueName), leagueLink];
	}, [bet, sportsbookRoutes, translations]);

	return shouldDeepLink && link ? (
		<TitleLink href={link}>
			<TitleLabel>{label}</TitleLabel>
		</TitleLink>
	) : (
		<TitleLabel>{label}</TitleLabel>
	);
};

export default BetRecommendationsBetTitle;
